import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Box, Button } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import './errorModal.css';

const ErrorModal = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState(t('error_modal_default_title'));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const handleApiError = (event) => {
      const { status, message, title, silent } = event.detail;

      // Background-polling endpoints (live sessions, notification settings)
      // opt out of the user-facing modal — they fire every 60s, so transient
      // 5xx blips would otherwise spam any long-open portal. Logtail still
      // receives the api-error event via the listener in index.js.
      if (silent) return;

      // Set appropriate title based on status code
      if (status === 403) {
        setErrorTitle(t('error_access_denied_title'));
        setErrorMessage(message || t('error_access_denied_message'));
      } else if (status === 500) {
        setErrorTitle(t('error_server_title'));
        setErrorMessage(message || t('error_server_message'));
      } else {
        setErrorTitle(title || t('error_modal_default_title'));
        setErrorMessage(message || t('error_unexpected_message'));
      }

      setOpen(true);
    };

    window.addEventListener('api-error', handleApiError);
    return () => window.removeEventListener('api-error', handleApiError);
  }, [t]);

  const handleClose = () => {
    setOpen(false);
    setErrorMessage("");
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: 4,
          padding: isMobile ? '20px' : '30px',
          width: isMobile ? '80%' : '90%',
          maxWidth: 400,
        }}
      >
        <div className="error-modal-header">
          <div className="error-modal-icon">
            <FontAwesomeIcon icon={faCircleExclamation} />
          </div>
          <h2>{errorTitle}</h2>
        </div>
        <p className="error-modal-message">{errorMessage}</p>
        <div className="error-modal-actions">
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              backgroundColor: '#5616ea',
              '&:hover': {
                backgroundColor: '#4a14d6',
              },
              textTransform: 'none',
              fontWeight: 500,
              padding: '8px 24px',
            }}
          >
            {t('ok')}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default ErrorModal;
